/**
 * PaywallOverlay - Overlay component for gated premium features
 *
 * Shows an upgrade prompt overlay when user tries to access a premium feature
 * without the required subscription tier.
 * Uses admin-configured pricing from platform settings.
 */

import { useState } from 'react';
import { Lock, Sparkles, Crown, Loader2, X, Check, Zap } from 'lucide-react';
import { type PremiumFeature, FEATURE_INFO, useSubscription } from '../../hooks/useSubscription';
import type { SubscriptionTier } from '@tss/shared';

interface PaywallOverlayProps {
  feature: PremiumFeature;
  onClose?: () => void;
  compact?: boolean;
}

export function PaywallOverlay({ feature, onClose, compact = false }: PaywallOverlayProps) {
  const { openCheckout, getTierPricing } = useSubscription();
  const [loading, setLoading] = useState<SubscriptionTier | null>(null);
  const [error, setError] = useState<string | null>(null);

  const featureInfo = FEATURE_INFO[feature];
  const recommendedTier = featureInfo.minimumTier;
  const recommendedPricing = getTierPricing(recommendedTier);
  const proPricing = getTierPricing('ATHLETE_PRO');

  async function handleUpgrade(targetTier: SubscriptionTier) {
    setLoading(targetTier);
    setError(null);
    try {
      await openCheckout(targetTier);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start checkout');
      setLoading(null);
    }
  }

  if (compact) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          background:
            'linear-gradient(135deg, rgba(var(--purple-rgb), 0.1) 0%, rgba(236, 72, 153, 0.1) 100%)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid rgba(var(--purple-rgb), 0.2)',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, var(--purple-500) 0%, #EC4899 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '12px',
          }}
        >
          <Lock size={24} color="white" />
        </div>
        <h4
          style={{
            fontWeight: '600',
            color: 'var(--gray-900)',
            marginBottom: '4px',
            fontSize: '15px',
          }}
        >
          {featureInfo.name}
        </h4>
        <p style={{ fontSize: '13px', color: 'var(--gray-500)', marginBottom: '16px' }}>
          {featureInfo.description}
        </p>
        <button
          onClick={() => handleUpgrade(recommendedTier)}
          disabled={loading !== null}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 16px',
            fontSize: '13px',
            background: 'linear-gradient(135deg, var(--purple-500) 0%, var(--purple-600) 100%)',
            color: 'white',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            cursor: 'pointer',
            fontWeight: '500',
          }}
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
          Upgrade to {recommendedPricing?.name || 'Basic'}
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.6)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        zIndex: 1100,
      }}
      onClick={onClose}
    >
      <div
        className="card"
        style={{
          width: '100%',
          maxWidth: '440px',
          padding: '0',
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with gradient */}
        <div
          style={{
            background: 'linear-gradient(135deg, var(--purple-500) 0%, #EC4899 100%)',
            padding: '32px 24px',
            textAlign: 'center',
            position: 'relative',
          }}
        >
          {onClose && (
            <button
              onClick={onClose}
              style={{
                position: 'absolute',
                top: '12px',
                right: '12px',
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              <X size={18} color="white" />
            </button>
          )}
          <div
            style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
            }}
          >
            <Crown size={32} color="white" />
          </div>
          <h2 style={{ color: 'white', fontSize: '22px', fontWeight: '700', marginBottom: '8px' }}>
            Unlock {featureInfo.name}
          </h2>
          <p style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '14px' }}>
            {featureInfo.description}
          </p>
        </div>

        {/* Content */}
        <div style={{ padding: '24px' }}>
          {error && (
            <div
              style={{
                padding: '12px',
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: 'var(--radius-md)',
                color: 'var(--color-danger)',
                fontSize: '13px',
                marginBottom: '16px',
              }}
            >
              {error}
            </div>
          )}

          {/* Recommended Plan */}
          <div
            style={{
              border: '2px solid var(--purple-500)',
              borderRadius: 'var(--radius-lg)',
              padding: '20px',
              marginBottom: '16px',
              position: 'relative',
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: '-10px',
                left: '50%',
                transform: 'translateX(-50%)',
                background: 'var(--purple-500)',
                color: 'white',
                padding: '2px 12px',
                borderRadius: '20px',
                fontSize: '11px',
                fontWeight: '600',
              }}
            >
              RECOMMENDED
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '12px',
              }}
            >
              <div>
                <h3 style={{ fontWeight: '700', fontSize: '18px', color: 'var(--gray-900)' }}>
                  {recommendedPricing?.name || 'Basic'}
                </h3>
                <p style={{ color: 'var(--gray-500)', fontSize: '13px' }}>
                  {recommendedPricing?.price_display || '$9.99'}/
                  {recommendedPricing?.interval || 'month'}
                </p>
              </div>
              <Sparkles size={24} style={{ color: 'var(--purple-500)' }} />
            </div>
            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 16px 0' }}>
              {(recommendedPricing?.features || []).slice(0, 5).map((feat, i) => (
                <li
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '13px',
                    color: 'var(--gray-700)',
                    marginBottom: '6px',
                  }}
                >
                  <Check size={14} style={{ color: 'var(--color-success)', flexShrink: 0 }} />
                  {feat}
                </li>
              ))}
              {recommendedPricing?.ai_credits_monthly && (
                <li
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '13px',
                    color: 'var(--gray-700)',
                    marginBottom: '6px',
                  }}
                >
                  <Check size={14} style={{ color: 'var(--color-success)', flexShrink: 0 }} />
                  {recommendedPricing.ai_credits_monthly} AI credits/month
                </li>
              )}
            </ul>
            <button
              onClick={() => handleUpgrade(recommendedTier)}
              disabled={loading !== null}
              style={{
                width: '100%',
                padding: '12px',
                background: 'linear-gradient(135deg, var(--purple-500) 0%, var(--purple-600) 100%)',
                color: 'white',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                fontWeight: '600',
                fontSize: '14px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              {loading === recommendedTier ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <>
                  <Zap size={18} />
                  Upgrade Now
                </>
              )}
            </button>
          </div>

          {/* Pro option if recommended is Basic */}
          {recommendedTier === 'ATHLETE_BASIC' && proPricing && (
            <button
              onClick={() => handleUpgrade('ATHLETE_PRO')}
              disabled={loading !== null}
              style={{
                width: '100%',
                padding: '12px',
                background: 'transparent',
                border: '1px solid var(--gray-200)',
                borderRadius: 'var(--radius-md)',
                color: 'var(--gray-700)',
                fontSize: '13px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
              }}
            >
              {loading === 'ATHLETE_PRO' ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <>
                  Or go Pro for {proPricing.price_display}/{proPricing.interval}
                </>
              )}
            </button>
          )}

          <p
            style={{
              textAlign: 'center',
              fontSize: '12px',
              color: 'var(--gray-400)',
              marginTop: '16px',
            }}
          >
            Cancel anytime. Billed monthly.
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * FeatureGate - Conditionally render content based on subscription
 *
 * Shows children if user has access, otherwise shows PaywallOverlay
 */
interface FeatureGateProps {
  feature: PremiumFeature;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export function FeatureGate({ feature, children, fallback }: FeatureGateProps) {
  const { canAccess, loading } = useSubscription();

  if (loading) {
    return null; // Or a loading state
  }

  if (canAccess(feature)) {
    return <>{children}</>;
  }

  return fallback ? <>{fallback}</> : <PaywallOverlay feature={feature} compact />;
}
